import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Clock } from 'lucide-react';
import { HoursBarChart } from '@/features/reports/components/HoursBarChart';
import { ProjectDonutChart } from '@/features/reports/components/ProjectDonutChart';
import { ProjectBreakdown } from '@/features/reports/components/ProjectBreakdown';
import {
  aggregateStackedByDay,
  aggregateByProject,
  totalDurationMinutes,
  type ReportTask,
} from '@/features/reports/utils/aggregateTasks';
import { formatDurationHms } from '@/utils/formatters';

interface ReportChartsProps {
  entries: readonly ReportTask[];
}

export function ReportCharts({ entries }: ReportChartsProps) {
  const { data: stackedByDay, projects: stackedProjects } = aggregateStackedByDay(entries);
  const byProject = aggregateByProject(entries);
  const totalMinutes = totalDurationMinutes(entries);

  return (
    <div className="space-y-6">
      <Card className="border-l-4 border-l-primary">
        <CardContent className="flex items-center gap-4 py-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface">
            <Clock className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Total hours</p>
            <p className="text-2xl font-semibold text-foreground" data-testid="report-total-hours">
              {formatDurationHms(totalMinutes)}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Hours by day</CardTitle>
        </CardHeader>
        <CardContent>
          <HoursBarChart data={stackedByDay} projects={stackedProjects} />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Hours by project</CardTitle>
          </CardHeader>
          <CardContent>
            <ProjectDonutChart data={byProject} totalMinutes={totalMinutes} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Project breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <ProjectBreakdown projects={byProject} totalMinutes={totalMinutes} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
